'use client';

// module imports
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// file imports
import { getFeedbackResponses } from '@/redux/slices/feedbackResponsesSlice';
import type { AppDispatch, RootState } from '@/redux/store';

interface DownloadAllResponsesProps {
  slug: string;
}

const DownloadAllResponses = ({ slug }: DownloadAllResponsesProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.feedbackResponses);
  const [isDownloading, setIsDownloading] = useState(false);

  const escapeCSVValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    // If value contains comma, newline, or quote, wrap in quotes and escape quotes
    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const handleDownloadResponsesCSV = async () => {
    setIsDownloading(true);
    try {
      // Fetch responses using Redux thunk
      const result = await dispatch(getFeedbackResponses(slug)).unwrap();
      const { questions, responses } = result;

      if (!responses || responses.length === 0) {
        alert('No feedback responses available to download.');
        setIsDownloading(false);
        return;
      }

      // Build content with each field on a new line
      const contentRows: string[] = [];

      responses.forEach((response: Record<string, any>, index: number) => {
        // Add separator between responses (except for first one)
        if (index > 0) {
          contentRows.push('');
          contentRows.push('---');
          contentRows.push('');
        }

        // Add basic info fields, each on a new line
        contentRows.push(`Submission ID: ${escapeCSVValue(response.submissionId || '')}`);
        contentRows.push(`Submitted At: ${escapeCSVValue(response.submittedAt ? new Date(response.submittedAt).toLocaleString() : '')}`);
        contentRows.push(`Status: ${escapeCSVValue(response.status || 'pending')}`);
        contentRows.push(`Name: ${escapeCSVValue(response.name || 'Anonymous')}`);
        contentRows.push(`Email: ${escapeCSVValue(response.email || '')}`);
        contentRows.push(`Phone Number: ${escapeCSVValue(response.phoneNumber || '')}`);
        contentRows.push(`Comments: ${escapeCSVValue(response.comments || '')}`);

        // Add answers for each question, each on a new line
        questions.forEach((question: { label: string }) => {
          const answer = response[question.label] || 'Skipped';
          contentRows.push(`${question.label}: ${escapeCSVValue(answer)}`);
        });
      });

      const csvContent = contentRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `feedback-responses-${slug}-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading responses CSV:', error);
      alert('Failed to download feedback responses. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const isLoading = loading || isDownloading;

  return (
    <button onClick={handleDownloadResponsesCSV} disabled={isLoading} className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed">
      {isLoading ? (
        <>
          <svg className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span className="hidden sm:inline">Loading...</span>
          <span className="sm:hidden">...</span>
        </>
      ) : (
        <>
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="hidden sm:inline">Download All Responses</span>
          <span className="sm:hidden">Responses</span>
        </>
      )}
    </button>
  );
};

export default DownloadAllResponses;
